import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import {environment} from '../../environments/environment';
import { sealToBase64 } from '../shared/hpke';

/**
 * The human-readable message from an error response body. Handles the common
 * shapes ({ message }, { error }, a plain string) and falls back to the
 * transport-level message when the backend didn't send one.
 */
function errorMessage(err: HttpErrorResponse): string {
  const body = err.error;
  if (typeof body === 'string' && body.trim()) {
    return body;
  }
  if (typeof body?.message === 'string' && body.message.trim()) {
    return body.message;
  }
  if (typeof body?.error === 'string' && body.error.trim()) {
    return body.error;
  }
  return err.message || 'Something went wrong. Please try again.';
}

/**
 * Attaches the access token to outgoing API requests, surfaces the error
 * message from the response body in a snackbar, and signs the user out on a
 * 401 (expired / invalid token).
 *
 * Requests to the auth service are left alone: they carry their own
 * credentials, and a 401 from them is a rejected sign-in rather than an
 * expired session.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const snackBar = inject(MatSnackBar);
  const isAuthEndpoint = req.url.startsWith(environment.authEndpoint);

  /** Show the response body's message, then rethrow so callers can still react. */
  const handleError = (err: HttpErrorResponse) => {
    snackBar.open(errorMessage(err), 'Dismiss', { duration: 5000 });
    // Not on the auth endpoint: signing out posts to /logout, so a 401 there
    // would re-enter this handler and loop.
    if (err.status === 401 && !isAuthEndpoint) {
      auth.logout();
    }
    return throwError(() => err);
  };

  // Seal credentials at the last point before the wire, so no caller can ship
  // a plaintext email or password — see PASSWORD_ENCRYPTION-v2.md.
  if (carriesCredentials(req)) {
    return sendWithSealedCredentials(req, next, auth).pipe(catchError(handleError));
  }

  // /public-key lands here: it is an auth endpoint but not a credential path,
  // so it is fetched unsealed and unauthenticated, and cannot recurse.
  if (isAuthEndpoint || !auth.token()) {
    return next(req).pipe(catchError(handleError));
  }

  return sendWithLiveToken(req, next, auth).pipe(catchError(handleError));
};

/** Auth-service paths whose credentials are sealed before they leave. */
const ENCRYPTED_PATHS = [
  environment.authLoginPath,
  environment.authMFAPath,
  environment.authPWReset,
  environment.authPWForgot
];

/**
 * Sealed wherever they appear, in a JSON body or a query parameter.
 *
 * `emailId` is here because the address is not named consistently across the
 * auth endpoints; whichever is present gets sealed. `otp` is deliberately
 * absent — the service expects it in plaintext.
 */
const ENCRYPTED_FIELDS = ['email', 'emailId', 'password'] as const;

function carriesCredentials(req: HttpRequest<unknown>): boolean {
  return ENCRYPTED_PATHS.some(path => req.url.startsWith(environment.authEndpoint + path));
}

/** The service's single, deliberately undifferentiated "could not decrypt". */
function isDecryptionFailure(err: HttpErrorResponse): boolean {
  return err.status === 400 && err.error?.code === 'AUTH_004';
}

/**
 * Returns the request with every credential field sealed.
 *
 * Body and query are both handled: password/forgot is a GET carrying the
 * address as a parameter. Angular percent-encodes the `+` in base64 and leaves
 * `/` and `=`, both legal raw in a query, so the default codec is correct here.
 */
async function sealCredentials(req: HttpRequest<unknown>, publicKey: string): Promise<HttpRequest<unknown>> {
  let sealedReq = req;

  const body = req.body as Record<string, unknown> | null;
  if (body && typeof body === 'object') {
    const sealedBody: Record<string, unknown> = { ...body };
    let changed = false;
    for (const field of ENCRYPTED_FIELDS) {
      const value = sealedBody[field];
      if (typeof value === 'string' && value.length > 0) {
        sealedBody[field] = await sealToBase64(publicKey, value);
        changed = true;
      }
    }
    if (changed) {
      sealedReq = sealedReq.clone({ body: sealedBody });
    }
  }

  let params = sealedReq.params;
  for (const field of ENCRYPTED_FIELDS) {
    const value = params.get(field);
    if (value) {
      params = params.set(field, await sealToBase64(publicKey, value));
    }
  }
  if (params !== sealedReq.params) {
    sealedReq = sealedReq.clone({ params });
  }

  return sealedReq;
}

/**
 * Seals the request's credentials and sends it; on the one failure the service
 * defines as "that key did not work", refetches the key and re-seals the
 * original plaintext exactly once.
 *
 * AUTH_004 is caught here rather than by the outer handler, so the user never
 * sees "Invalid request." flash for a failure we are about to recover from.
 * The retry has no catch of its own, so a second failure surfaces normally.
 */
function sendWithSealedCredentials(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  auth: AuthService
): Observable<HttpEvent<unknown>> {
  // `req` still holds the plaintext, so the retry seals from source — a blob is
  // bound to one key, and resending the sent one would just fail again.
  const attempt = () => auth.encryptionPublicKey().pipe(
    switchMap(publicKey => sealCredentials(req, publicKey)),
    switchMap(sealed => next(sealed))
  );

  return attempt().pipe(
    catchError((err: HttpErrorResponse) => {
      if (!isDecryptionFailure(err)) {
        return throwError(() => err);
      }
      auth.invalidateEncryptionKey();
      return attempt();
    })
  );
}

/**
 * Sends the request with a live access token, refreshing when it isn't one.
 *
 * Two paths, because two things can be true. We may already know the token is
 * dead — straight after a page load, where the monotonic clock has reset and
 * nothing has anchored it yet — in which case spending a round trip to
 * discover that is waste. Or the server may reject a token we believed was
 * live: revoked early, or signed with a rotated key.
 *
 * refreshSession() is shared, so a page load firing several requests at once
 * produces one refresh rather than one per request.
 */
function sendWithLiveToken(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  auth: AuthService
): Observable<HttpEvent<unknown>> {
  // Read inside send() so a retry picks up the refreshed token rather than the
  // dead one this closure was built with.
  const send = () => next(req.clone({
    setHeaders: {
      Authorization: `Bearer ${auth.token()}`,
      // 'X-Server-Region': auth.region()
    }
  }));

  if (!auth.hasLiveAccessToken()) {
    // Beyond recovery: let it 401 and fall through to the logout in
    // handleError, rather than adding a second path that signs the user out.
    return auth.canRefresh() ? auth.refreshSession().pipe(switchMap(send)) : send();
  }

  return send().pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status !== 401 || !auth.canRefresh()) {
        return throwError(() => err);
      }
      return auth.refreshSession().pipe(switchMap(send));
    })
  );
}
